import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AppShell from '../components/AppShell';
import Button from '../components/Button';
import Callout from '../components/Callout';
import { VERTICALS } from '../data/verticals';
import { api, type ProjectSummary } from '../lib/api';
import { useAuth } from '../lib/useAuth';

const LABEL = new Map(VERTICALS.map((v) => [v.id, v.label]));

/**
 * Where a project should resume.
 *
 * Read off the data rather than off `status`, because status is advanced by
 * whichever screen last saved and lags whenever someone navigates backwards to
 * change an answer. The data cannot lie about which step is unfinished.
 */
function resumeAt(p: ProjectSummary): { path: string; label: string } {
  const id = p.id;
  if (!p.verticalId) return { path: `/project/${id}/questions`, label: 'Start' };
  if (p.answeredCount === 0) return { path: `/project/${id}/questions`, label: 'Answer the questions' };
  if (p.sizeSf === null) return { path: `/project/${id}/size`, label: 'Set the size' };
  if (!p.constructionSystem) return { path: `/project/${id}/system`, label: 'Choose how it is built' };
  if (p.status === 'styles') return { path: `/project/${id}/styles`, label: 'Pick the photographs' };
  if (p.status === 'results' || p.status === 'complete') {
    return { path: `/project/${id}/results`, label: 'Open' };
  }
  return { path: `/project/${id}/styles`, label: 'Pick the photographs' };
}

/** "3 days ago" beats a timestamp for a list you are scanning. */
function ago(iso: string): string {
  const then = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z').getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 31) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.round(days / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .listProjects()
      .then((d) => !cancelled && setProjects(d.projects))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AppShell>
      <p className="t-eyebrow text-green-600">Signed in</p>
      <h1 className="t-h1 mt-3 text-ink">
        {user?.name ? `Welcome, ${user.name.split(' ')[0]}` : 'Welcome'}
      </h1>
      <p className="t-body-lg measure mt-4 text-grey-600">
        Start a project and we will walk you through it: a few questions, a size, how it
        gets built, then the buildings you like.
      </p>

      <div className="mt-10 flex flex-wrap gap-3">
        <Link to="/new">
          <Button>Start a project</Button>
        </Link>
        {user?.role === 'admin' && (
          <>
            <Link to="/admin/requests">
              <Button variant="secondary">Access requests</Button>
            </Link>
            <Link to="/admin/styles">
              <Button variant="secondary">Style library</Button>
            </Link>
          </>
        )}
      </div>

      <section className="mt-14">
        <h2 className="t-h2 text-ink">Your projects</h2>

        {failed && (
          <div className="mt-6 max-w-xl">
            <Callout tone="caution" title="We could not load your projects">
              The list did not come back. Reload the page — nothing has been lost.
            </Callout>
          </div>
        )}

        {!failed && projects === null && (
          <p className="t-body mt-4 text-grey-600">Loading…</p>
        )}

        {!failed && projects?.length === 0 && (
          <div className="mt-6 max-w-xl">
            <Callout title="Nothing here yet">
              Start a project and it will show up in this list, so you can leave it and
              come back without keeping the link.
            </Callout>
          </div>
        )}

        {!failed && projects && projects.length > 0 && (
          <ul className="mt-6 flex flex-col gap-3">
            {projects.map((p) => {
              const next = resumeAt(p);
              return (
                <li key={p.id}>
                  <Link
                    to={next.path}
                    className="flex flex-col gap-3 rounded-lg border border-grey-200 bg-white p-5 shadow-sm transition-colors hover:border-green-600 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <span className="min-w-0">
                      <span className="t-h3 block truncate text-ink">
                        {p.name ?? LABEL.get(p.verticalId ?? '') ?? 'Untitled project'}
                      </span>
                      <span className="t-meta mt-1 block text-grey-600">
                        {p.name && p.verticalId ? `${LABEL.get(p.verticalId)} · ` : ''}
                        {p.sizeSf ? `${p.sizeSf.toLocaleString('en-US')} sf · ` : ''}
                        {ago(p.updatedAt)}
                      </span>
                    </span>
                    <span className="t-meta min-h-tap inline-flex shrink-0 items-center rounded-full border border-grey-300 px-5 font-semibold text-green-700">
                      {next.label}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </AppShell>
  );
}
